<template>
    <pendo-multiselect
        class="app-select"
        :value="value"
        full-width
        :append-to-body="false"
        :disabled="readOnly"
        :allow-empty="false"
        :options="options"
        :preselect-first="preselectFirst"
        label-key="displayName"
        value-key="id"
        :no-results-text="$t('appSelect.No matching Apps found.')"
        :placeholder="$t('appSelect.Select App')"
        @select="onAppSelect">
        <template #selectedLabel="{ option }">
            <pendo-app-display :apps="option" />
        </template>
        <template #option="{ option }">
            <pendo-app-display :apps="option" />
        </template>
    </pendo-multiselect>
</template>

<script>
import { PendoMultiselect, PendoAppDisplay } from '@pendo/components';
import get from 'lodash/get';
import { isMobileApp, getAppIconType } from '@/utils/apps';
import { APP_SPECIFIC_AUTO_METADATA_IDS } from '@/utils/segments.js';
import isUndefined from 'lodash/isUndefined';
import { AppsService as Apps } from '@/common/apps/apps.service';
const { MOBILE_PLATFORM, MOBILE_PLATFORMS } = Apps;

export default {
    name: 'AppSelector',
    components: {
        PendoAppDisplay,
        PendoMultiselect,
    },
    props: {
        rule: {
            type: Object,
            required: true,
        },
        apps: {
            type: Array,
            default: () => [],
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        showPlatformGroupOptions: {
            type: Boolean,
            default: false,
        },
        preselectFirst: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['input'],
    computed: {
        appOptions () {
            let { apps } = this;
            const platforms = get(this, 'rule.platforms');
            if (platforms) {
                apps = apps.filter((app) => platforms.includes(app.platform));
            }

            if (this.isSessionPropertyRule) {
                const excludedAppTypes = [...MOBILE_PLATFORMS, MOBILE_PLATFORM];
                apps = apps.filter(({ platform }) => !excludedAppTypes.includes(platform));
            }

            return apps
                .map((app) => {
                    const { platform, displayName, id, framework, applicationFlags, trainingAttributes } = app;

                    return {
                        id,
                        displayName,
                        platform,
                        icon: {
                            type: getAppIconType(platform),
                        },
                        framework,
                        applicationFlags,
                        trainingAttributes,
                    };
                })
                .sort((a, b) => {
                    const aPlatform = a.platform.toLowerCase();
                    const bPlatform = b.platform.toLowerCase();
                    const aName = a.displayName.toLowerCase();
                    const bName = b.displayName.toLowerCase();

                    if (aPlatform > bPlatform) {
                        return -1;
                    }

                    if (aPlatform < bPlatform) {
                        return 1;
                    }

                    if (aName > bName) {
                        return 1;
                    }

                    if (aName < bName) {
                        return -1;
                    }

                    return 1;
                });
        },
        appGroupOptions () {
            const hasMobileApps = this.appOptions.some((option) => isMobileApp(option.platform));
            const isNotUserAgentRule = get(this, 'rule.type') !== 'useragent';
            const hasMobileAppsAndIsNotUserAgentRule = hasMobileApps && isNotUserAgentRule;
            const isAppSpecificAutoMetadata = APP_SPECIFIC_AUTO_METADATA_IDS.includes(this.rule.id);

            const options = this.isSessionPropertyRule
                ? []
                : [
                    {
                        id: '*',
                        displayName: this.$t('appSelect.All Apps'),
                    },
                ];

            if (hasMobileAppsAndIsNotUserAgentRule && !isAppSpecificAutoMetadata) {
                options.push(
                    {
                        id: 'web',
                        displayName: this.$t('appSelect.Web Apps'),
                    },
                    {
                        id: 'extension',
                        displayName: this.$t('appSelect.Extension Apps'),
                    },
                    {
                        id: 'mobile',
                        displayName: this.$t('appSelect.Mobile Apps'),
                    },
                );
            } else if (this.isSessionPropertyRule) {
                options.push(
                    {
                        id: 'web|extension',
                        displayName: this.$t('appSelect.Web & Extension Apps'),
                    },
                    {
                        id: 'web',
                        displayName: this.$t('appSelect.Web Apps'),
                    },
                    {
                        id: 'extension',
                        displayName: this.$t('appSelect.Extension Apps'),
                    },
                );
            }

            return options;
        },
        options () {
            if (this.showPlatformGroupOptions) {
                return [
                    {
                        label: 'default',
                        options: this.appGroupOptions,
                    },
                    {
                        label: 'divider',
                        options: this.appOptions,
                    },
                ];
            }

            return this.appOptions;
        },
        value () {
            if (this.showPlatformGroupOptions) {
                let { appId } = this.rule;

                if (isUndefined(appId)) {
                    appId = this.isSessionPropertyRule ? 'web|extension' : '*';
                }

                return this.options
                    .reduce((acc, option) => {
                        return acc.concat(option.options);
                    }, [])
                    .find((option) => option.id === appId);
            }

            return this.options.find((option) => option.id === this.rule.appId);
        },
        isSessionPropertyRule () {
            return this.rule.id === 'utm';
        },
    },
    methods: {
        onAppSelect (app) {
            this.$emit('input', { field: 'appId', value: app.id });
        },
    },
};
</script>
<style scoped lang="less">
    .app-select {
        min-width: 0;
        max-width: 100%;
    }
</style>